import React, { memo, useEffect, useRef } from 'react'
import propTypes from 'prop-types'
import { SketchPicker } from 'react-color'
import Button from '../Button'
import ModalContainer from '../ModalContainer'
import './styles.scss'

const presetColors = [
  { color: '#D0021B', title: 'Punainen, #D0021B' },
  { color: '#F5A623', title: 'Oranssi, #F5A623' },
  { color: '#F8E71C', title: 'Keltainen, #F8E71C' },
  { color: '#8B572A', title: 'Ruskea, #8B572A' },
  { color: '#7ED321', title: 'Vaaleanvihreä, #7ED321' },
  { color: '#417505', title: 'Vihreä, #417505' },
  { color: '#BD10E0', title: 'Violetti, #BD10E0' },
  { color: '#9013FE', title: 'Sinivioletti, #9013FE' },
  { color: '#4A90E2', title: 'Sininen, #4A90E2' },
  { color: '#50E3C2', title: 'Turkoosi, #50E3C2' },
  { color: '#B8E986', title: 'Vaalea vihreä, #B8E986' },
  { color: '#4A4A4A', title: 'Tummanharmaa, #4A4A4A' },
  { color: '#9B9B9B', title: 'Harmaa, #9B9B9B' },
  { color: '#000000', title: 'Musta, #000000' },
  { color: '#FFFFFF', title: 'Valkoinen, #FFFFFF' },
]

const colorAliases = {
  black: '#000000',
  white: '#ffffff',
  transparent: 'läpinäkyvä',
}

const readableColor = (value) => {
  if (!value) return 'ei valittu'
  if (value === 'transparent') return 'läpinäkyvä'
  return value
}

const normalizeColor = (value) => {
  const text = String(value || '')
    .trim()
    .toLowerCase()
  return colorAliases[text] || text
}

const ColorPicker = ({
  textbox,
  openColorModal,
  setOpenColorModal,
  changeFont,
  pageBackground,
  cancelAndCloseColorPicker,
  color,
  setColor,
  chooseColor,
}) => {
  const pickerRef = useRef(null)
  const heading = pageBackground
    ? 'Valitse sivun taustaväri'
    : changeFont
    ? 'Valitse tekstin väri'
    : 'Valitse taustaväri'
  const selectedColorText = readableColor(color)
  const selectedValueText = pageBackground
    ? `Valittu sivun taustaväri ${selectedColorText}`
    : `Valittu väri ${selectedColorText}`
  const swatchGroupLabel = pageBackground
    ? 'Sivun taustavärivaihtoehdot'
    : changeFont
    ? 'Tekstin värivaihtoehdot'
    : 'Taustavärivaihtoehdot'

  const handleColorChange = (nextColor) => {
    if (nextColor === 'transparent') {
      setColor(nextColor)
    } else {
      setColor(nextColor.hex)
    }
  }

  useEffect(() => {
    let observer
    let frame
    let attempts = 0

    const labelPicker = () => {
      const root = pickerRef.current
      if (!root) return
      const picker = root.querySelector('.sketch-picker')
      if (!picker) return

      const saturation = picker.children[0]
      if (saturation) {
        saturation.setAttribute('role', 'group')
        saturation.setAttribute(
          'aria-label',
          `Kylläisyys ja kirkkaus. ${selectedValueText}`
        )
      }

      const hue =
        picker.children[1] && picker.children[1].children[0]
          ? picker.children[1].children[0].children[0]
          : null
      if (hue) {
        hue.setAttribute('role', 'group')
        hue.setAttribute('aria-label', `Sävy. ${selectedValueText}`)
      }

      const areas = picker.querySelectorAll('.flexbox-fix')
      const swatchList = areas[areas.length - 1]
      if (!swatchList) return
      swatchList.setAttribute('role', 'radiogroup')
      swatchList.setAttribute('aria-label', swatchGroupLabel)
      swatchList.querySelectorAll('[tabindex="0"]').forEach((swatch) => {
        const name = swatch.getAttribute('title') || 'Väri'
        const hex = name.includes('#') ? name.slice(name.indexOf('#')) : name
        swatch.setAttribute('role', 'radio')
        swatch.setAttribute('aria-label', name)
        swatch.setAttribute(
          'aria-checked',
          normalizeColor(hex) === normalizeColor(color) ? 'true' : 'false'
        )
      })
    }

    const onKeyDown = (event) => {
      if (event.key !== ' ' && event.code !== 'Space') return
      const root = pickerRef.current
      const swatch = event.target.closest('.sketch-picker [tabindex="0"]')
      if (!root || !swatch || !root.contains(swatch)) return
      event.preventDefault()
      swatch.click()
    }

    const start = () => {
      const root = pickerRef.current
      if (!root || !root.querySelector('.sketch-picker')) {
        attempts += 1
        if (attempts < 10) frame = requestAnimationFrame(start)
        return
      }
      labelPicker()
      root.addEventListener('keydown', onKeyDown)
      observer = new MutationObserver(labelPicker)
      observer.observe(root, { childList: true, subtree: true })
    }

    start()
    return () => {
      if (frame) cancelAnimationFrame(frame)
      if (observer) observer.disconnect()
      const root = pickerRef.current
      if (root) root.removeEventListener('keydown', onKeyDown)
    }
  }, [color, selectedValueText, swatchGroupLabel])

  return (
    <ModalContainer
      modalIsOpen={openColorModal}
      closeModal={() => setOpenColorModal(false)}
      label={heading}
      info
      className="color-modal"
    >
      <div className="color-picker-content" ref={pickerRef}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: '5%',
            marginBottom: '5%',
          }}
        >
          <h1 className="color-picker-header">{heading}</h1>
          <div className="swatch-container">
            <div
              style={{
                width: '18px',
                height: '20px',
                borderRadius: '2px',
                background: color,
              }}
            />
          </div>
        </div>
        <p className="selected-color-value" aria-live="polite">
          {selectedValueText}
        </p>
        <SketchPicker
          color={color}
          onChangeComplete={handleColorChange}
          disableAlpha
          width="100%"
          presetColors={presetColors}
        />
        {!changeFont && textbox && (
          <button
            type="button"
            className="dialogue-button gray-button single-button"
            onClick={() => handleColorChange('transparent')}
            aria-pressed={color === 'transparent'}
          >
            Läpinäkyvä tausta
          </button>
        )}
        <div className="color-palette-buttons-container">
          <Button onClick={chooseColor} className="dialogue-button select-button">
            Valitse
          </Button>
          <Button
            onClick={cancelAndCloseColorPicker}
            className="dialogue-button gray-button"
          >
            Peruuta
          </Button>
        </div>
      </div>
    </ModalContainer>
  )
}

ColorPicker.propTypes = {
  textbox: propTypes.instanceOf(Object),
  openColorModal: propTypes.bool.isRequired,
  setOpenColorModal: propTypes.func.isRequired,
  changeFont: propTypes.bool.isRequired,
  pageBackground: propTypes.bool,
  cancelAndCloseColorPicker: propTypes.func,
  color: propTypes.string,
  setColor: propTypes.func.isRequired,
  chooseColor: propTypes.func.isRequired,
}

ColorPicker.defaultProps = {
  textbox: null,
  color: null,
  changeFont: false,
  pageBackground: false,
}

export default memo(ColorPicker)
